import React, { memo, useEffect, useLayoutEffect, useState } from 'react';

import { AppInstallBannerFeaturePromptEvent } from './types';

import I18n from '@wetransfer/transfer/modules/i18n';
import { MOBILE_SUBDOMAIN_ENDPOINTS } from '@wetransfer/transfer/constant/app-subdomain-environment';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';

import './AppInstallBanner.scss';
import browser from '@wetransfer/transfer/modules/concorde-browser/browser';
import { isProductionEnv } from '@wetransfer/transfer/modules/url';

export interface AppInstallBannerProps {
  id: string;
  secret: string;
  snowplowProperty: 'download_page' | 'download_started' | 'upload_page';
  amplitudeContext: AppInstallBannerFeaturePromptEvent['additionalProperties']['Context'];
  view: 'send' | 'receive';
  className?: string;
}

const redirectToMobileAppDomain = (
  view: AppInstallBannerProps['view'],
  id?: string,
  secret?: string
) => {
  const endpoint = isProductionEnv()
    ? MOBILE_SUBDOMAIN_ENDPOINTS.PRODUCTION
    : MOBILE_SUBDOMAIN_ENDPOINTS.STAGING;

  // Download context
  if (view === 'receive') {
    window.location.replace(`${endpoint}/downloads/${id}/${secret}`);

    return;
  }

  // Upload context
  window.location.replace(`${endpoint}/upload`);
};

const appStoreBadges = {
  ios: {
    alt: 'Apple App Store',
    image: I18n.image('vendors/app_store_download.svg'),
  },
  android: {
    alt: 'Google Play Store',
    image: I18n.image('vendors/google_play_download.svg'),
  },
} as const;

export const AppInstallBanner: React.FC<AppInstallBannerProps> = memo(
  ({ id, secret, snowplowProperty, amplitudeContext, view }) => {
    useEffect(() => {
      Analytics.trackSnowplowEvent({
        category: 'transfer',
        action: 'show_app_banner',
        property: snowplowProperty,
      });

      const amplitudeEvent: AppInstallBannerFeaturePromptEvent = {
        action: 'Feature Prompt',
        additionalProperties: {
          Action: 'Shown',
          Feature: 'Mobile App',
          Component: 'Banner',
          Context: amplitudeContext,
        },
      };

      trackAmplitudeEvent(amplitudeEvent);
    }, [snowplowProperty, amplitudeContext]);

    const [platform, setPlatform] = useState<'android' | 'ios'>('ios');

    useLayoutEffect(() => {
      document.body.classList.add('app-install-banner-visible');
      setPlatform(browser.isAndroid ? 'android' : 'ios');

      return () => {
        document.body.classList.remove('app-install-banner-visible');
      };
    }, [setPlatform]);

    function handleClick() {
      Analytics.trackSnowplowEvent({
        category: 'transfer',
        action: 'click_app_banner',
        property: snowplowProperty,
      });

      const amplitudeEvent: AppInstallBannerFeaturePromptEvent = {
        action: 'Feature Prompt',
        additionalProperties: {
          Action: 'Confirmed',
          Feature: 'Mobile App',
          Component: 'Banner',
          Context: amplitudeContext,
        },
      };

      trackAmplitudeEvent(amplitudeEvent);
      redirectToMobileAppDomain(view, id, secret);
    }

    return (
      <div className="AppInstallBanner" onClick={handleClick} role="none">
        <p className="AppInstallBanner__text">
          {view === 'send'
            ? I18n.t('transfer.mobile_web.ios_banner_prompt_send')
            : I18n.t('transfer.mobile_web.ios_banner_prompt')}
        </p>
        <img
          className="AppInstallBanner__image"
          src={appStoreBadges[platform].image}
          alt={appStoreBadges[platform].alt}
        />
      </div>
    );
  }
);

AppInstallBanner.displayName = 'AppInstallBanner';

export default AppInstallBanner;
